import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs, doc, getDoc, query, orderBy, limit } from "firebase/firestore";
import {
    FiTrendingUp, FiMapPin, FiBookOpen, FiActivity, FiGlobe, FiDollarSign,
    FiShoppingBag, FiPieChart, FiPackage, FiDownload
} from "react-icons/fi";
import toast from "react-hot-toast";

// Orders that count as money received / still owed / lost
const PAID_STATUSES = ["paid", "processing", "shipped", "delivered"];
const PENDING_STATUSES = ["pending", "pending_ath_verification"];
const STATUS_LABELS = {
    pending: "Pending",
    pending_ath_verification: "Pending ATH verification",
    paid: "Paid",
    processing: "Processing",
    shipped: "Shipped",
    delivered: "Delivered",
    cancelled: "Cancelled",
};
const LOW_STOCK = 5;

function dateOf(order) {
    return order.createdAt?.toDate ? order.createdAt.toDate() : null;
}

function dayKey(d) {
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}

function money(n) {
    return `$${(Number(n) || 0).toFixed(2)}`;
}

function downloadCsv(filename, headers, rows) {
    const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map(r => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function AdminReports() {
    const [view, setView] = useState("sales"); // traffic, sales, inventory
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [allProducts, setAllProducts] = useState({});
    const [allOrders, setAllOrders] = useState([]);

    const [dateFilter, setDateFilter] = useState("today"); // today, yesterday, month, custom, all
    const [customRange, setCustomRange] = useState({ start: "", end: "" });

    useEffect(() => {
        async function loadData() {
            setLoading(true);
            try {
                // 1. Products (names, categories, variants and stock for inventory)
                const prodSnap = await getDocs(collection(db, "products"));
                const pMap = {};
                prodSnap.forEach(d => {
                    const p = d.data();
                    pMap[d.id] = {
                        title: p.title,
                        category: p.category || "Uncategorized",
                        variants: p.variants || [],
                        format: p.format,
                        stock: p.stock,
                        isDigital: p.isDigital,
                    };
                });
                setAllProducts(pMap);

                // 2. Traffic stats (today)
                const dayId = new Date().toISOString().split("T")[0];
                const todaySnap = await getDoc(doc(db, "analytics", dayId));
                if (todaySnap.exists()) setStats(todaySnap.data());

                // 3. Traffic history (last 30 days)
                const qTraffic = query(collection(db, "analytics"), orderBy("__name__", "desc"), limit(30));
                const historySnap = await getDocs(qTraffic);
                setHistory(historySnap.docs.map(d => ({ id: d.id, ...d.data() })));

                // 4. Orders
                const qOrders = query(collection(db, "orders"), orderBy("createdAt", "desc"));
                const ordersSnap = await getDocs(qOrders);
                setAllOrders(ordersSnap.docs.map(d => ({ id: d.id, ...d.data() })));

            } catch (err) {
                console.error("FAILED_TO_LOAD_REPORTS:", err);
                if (err.message && err.message.includes("index")) {
                    console.warn("Firestore Index Required: Check the link in the error above.");
                }
                toast.error(`Could not load reports: ${err.message || "Unknown error"}`);
            }
            setLoading(false);
        }
        loadData();
    }, []);

    if (loading) return <div className="admin-loading">Calculating analytics…</div>;

    // ── Date range (shared by Sales and Inventory sold counts) ──
    function getRange() {
        const now = new Date();
        if (dateFilter === "today") {
            const start = new Date(now); start.setHours(0, 0, 0, 0);
            return { start, end: null };
        }
        if (dateFilter === "yesterday") {
            const start = new Date(now); start.setDate(start.getDate() - 1); start.setHours(0, 0, 0, 0);
            const end = new Date(start); end.setHours(23, 59, 59, 999);
            return { start, end };
        }
        if (dateFilter === "month") {
            const start = new Date(now); start.setDate(1); start.setHours(0, 0, 0, 0);
            return { start, end: null };
        }
        if (dateFilter === "custom" && customRange.start && customRange.end) {
            const start = new Date(customRange.start + "T00:00:00");
            const end = new Date(customRange.end + "T23:59:59.999");
            return { start, end };
        }
        return { start: null, end: null }; // all time
    }

    const range = getRange();
    const inRange = (d) => {
        if (!range.start && !range.end) return true;
        if (!d) return false;
        return (!range.start || d >= range.start) && (!range.end || d <= range.end);
    };

    // ── Traffic Processing ──────────────────────────────────
    const decodePath = (key) => key.replace(/\$/g, '/').replace(/home/g, '/');

    // Filter history based on date selection
    const filteredHistory = history.filter(h => {
        const d = new Date(h.id); d.setHours(23, 59, 59, 999);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1); yesterday.setHours(0, 0, 0, 0);

        if (dateFilter === "today") return h.id === today.toISOString().split("T")[0];
        if (dateFilter === "yesterday") return h.id === yesterday.toISOString().split("T")[0];
        if (dateFilter === "month") {
            const start = new Date(); start.setDate(1); start.setHours(0, 0, 0, 0);
            return d >= start;
        }
        if (dateFilter === "custom" && customRange.start && customRange.end) {
            const s = new Date(customRange.start); s.setHours(0, 0, 0, 0);
            const e = new Date(customRange.end); e.setHours(23, 59, 59, 999);
            return d >= s && d <= e;
        }
        return true;
    });

    // Aggregate Traffic Stats from filtered history
    const trafficAgg = {
        totalVisits: 0,
        pages: {},
        countries: {},
        products: {},
        lastUpdated: stats?.lastUpdated
    };

    filteredHistory.forEach(h => {
        trafficAgg.totalVisits += (h.totalVisits || 0);

        // Handle nested maps (New Format)
        Object.entries(h.pages || {}).forEach(([k, v]) => trafficAgg.pages[k] = (trafficAgg.pages[k] || 0) + v);
        Object.entries(h.countries || {}).forEach(([k, v]) => trafficAgg.countries[k] = (trafficAgg.countries[k] || 0) + v);
        Object.entries(h.products || {}).forEach(([k, v]) => trafficAgg.products[k] = (trafficAgg.products[k] || 0) + v);

        // Fallback for old flat-key format (e.g. "pages.home")
        Object.entries(h).forEach(([key, val]) => {
            if (key.startsWith("pages.") && !h.pages) {
                const subKey = key.split("pages.")[1];
                trafficAgg.pages[subKey] = (trafficAgg.pages[subKey] || 0) + val;
            }
            if (key.startsWith("countries.") && !h.countries) {
                const subKey = key.split("countries.")[1];
                trafficAgg.countries[subKey] = (trafficAgg.countries[subKey] || 0) + val;
            }
            if (key.startsWith("products.") && !h.products) {
                const subKey = key.split("products.")[1];
                trafficAgg.products[subKey] = (trafficAgg.products[subKey] || 0) + val;
            }
        });
    });

    const sortedPages = Object.entries(trafficAgg.pages).sort((a, b) => b[1] - a[1]);
    const sortedCountries = Object.entries(trafficAgg.countries).sort((a, b) => b[1] - a[1]);
    const sortedProducts = Object.entries(trafficAgg.products).sort((a, b) => b[1] - a[1]);

    // ── Sales Processing ─────────────────────────────────────
    const filteredOrders = allOrders.filter(o => inRange(dateOf(o)));

    const sales = {
        revenue: 0,        // money received (paid + later statuses)
        awaiting: 0,       // pending / awaiting verification
        cancelledCount: 0,
        paidCount: 0,
        byStatus: {},      // { status: { count, amount } }
        byDay: {},         // { "YYYY-MM-DD": { orders, paid, pending, cancelled } }
        byPayment: {},     // { method: { count, paid } }
        products: {},      // { title: units }
        categories: {},    // { category: units }
        locations: {},     // { "city, country": orders }
    };

    filteredOrders.forEach(o => {
        const status = o.status || "pending";
        const amount = Number(o.total) || 0;

        sales.byStatus[status] = sales.byStatus[status] || { count: 0, amount: 0 };
        sales.byStatus[status].count += 1;
        sales.byStatus[status].amount += amount;

        if (PAID_STATUSES.includes(status)) { sales.revenue += amount; sales.paidCount += 1; }
        if (PENDING_STATUSES.includes(status)) sales.awaiting += amount;
        if (status === "cancelled") sales.cancelledCount += 1;

        const d = dateOf(o);
        const key = d ? dayKey(d) : "";
        if (!sales.byDay[key]) sales.byDay[key] = { date: key, orders: 0, paid: 0, pending: 0, cancelled: 0 };
        const day = sales.byDay[key];
        day.orders += 1;
        if (PAID_STATUSES.includes(status)) day.paid += amount;
        if (PENDING_STATUSES.includes(status)) day.pending += amount;
        if (status === "cancelled") day.cancelled += 1;

        const method = o.paymentMethod || "Unknown";
        if (!sales.byPayment[method]) sales.byPayment[method] = { count: 0, paid: 0 };
        sales.byPayment[method].count += 1;
        if (PAID_STATUSES.includes(status)) sales.byPayment[method].paid += amount;

        // Products, categories and locations only count orders that were not cancelled
        if (status === "cancelled") return;

        (o.items || []).forEach(item => {
            const pInfo = allProducts[item.id] || { title: item.title, category: "Uncategorized" };
            sales.products[pInfo.title] = (sales.products[pInfo.title] || 0) + (item.quantity || 1);
            sales.categories[pInfo.category] = (sales.categories[pInfo.category] || 0) + (item.quantity || 1);
        });

        const loc = o.shippingCity ? `${o.shippingCity}, ${o.shippingCountry}` : (o.address || "Digital/Unknown");
        sales.locations[loc] = (sales.locations[loc] || 0) + 1;
    });

    const dayRows = Object.values(sales.byDay).sort((a, b) => {
        if (a.date === "") return 1;
        if (b.date === "") return -1;
        return b.date.localeCompare(a.date);
    });
    const maxDayPaid = Math.max(0, ...dayRows.map(r => r.paid));
    const statusRows = Object.keys(STATUS_LABELS).filter(s => sales.byStatus[s]).map(s => ({ status: s, ...sales.byStatus[s] }));
    const paymentRows = Object.entries(sales.byPayment).sort((a, b) => b[1].paid - a[1].paid || b[1].count - a[1].count);
    const avgOrder = sales.paidCount > 0 ? sales.revenue / sales.paidCount : 0;

    const topSalesProducts = Object.entries(sales.products).sort((a, b) => b[1] - a[1]);
    const topSalesLocations = Object.entries(sales.locations).sort((a, b) => b[1] - a[1]);
    const topSalesCategories = Object.entries(sales.categories).sort((a, b) => b[1] - a[1]);

    // ── Inventory Processing ─────────────────────────────────
    // Units sold in the selected period, matched to a specific variant
    function unitsSold(productId, format, delivery) {
        let units = 0;
        filteredOrders.forEach(o => {
            if (o.status === "cancelled") return;
            (o.items || []).forEach(item => {
                if (item.id !== productId) return;
                const itemFormat = item.format || (item.isDigital ? "eBook" : "Paperback");
                if (itemFormat !== format) return;
                if (delivery && item.bundleDelivery && item.bundleDelivery !== delivery) return;
                units += item.quantity || 1;
            });
        });
        return units;
    }

    const inventoryRows = [];
    Object.entries(allProducts).forEach(([productId, p]) => {
        const variants = p.variants && p.variants.length > 0
            ? p.variants
            : [{ format: p.format || "Paperback", stock: p.stock, isDigital: p.isDigital }];

        variants.forEach(v => {
            // Bundles have no stock of their own; their books are listed instead
            if (v.format === "Bundle") return;
            const isBundle = v.format === "Bundle";
            const isDigital = v.format === "eBook" || v.format === "Audiobook" || !!v.isDigital || (isBundle && v.bundleDelivery === "Digital");
            const isCombo = isBundle && v.bundleDelivery === "Combo";
            const hasStock = v.stock !== undefined && v.stock !== null && v.stock !== "";
            const stock = isDigital || !hasStock ? null : Number(v.stock);

            let status = "ok";
            if (isDigital) status = "digital";
            else if (stock === null) status = "unknown";
            else if (stock <= 0) status = "out";
            else if (stock <= LOW_STOCK) status = "low";

            inventoryRows.push({
                key: `${productId}|${v.format}|${v.bundleDelivery || ""}`,
                title: p.title || productId,
                format: v.format,
                type: isDigital ? "Digital" : isCombo ? "Physical + Digital" : isBundle ? `Bundle (${v.bundleDelivery || "Physical"})` : "Physical",
                stock,
                sold: unitsSold(productId, v.format, isBundle ? v.bundleDelivery : null),
                status,
            });
        });
    });

    const rankOrder = { out: 0, low: 1, unknown: 2, ok: 3, digital: 4 };
    inventoryRows.sort((a, b) => rankOrder[a.status] - rankOrder[b.status] || a.title.localeCompare(b.title));

    const invSummary = {
        outOfStock: inventoryRows.filter(r => r.status === "out").length,
        lowStock: inventoryRows.filter(r => r.status === "low").length,
        physicalUnits: inventoryRows
            .filter(r => r.stock !== null && !r.format.includes("Bundle"))
            .reduce((s, r) => s + r.stock, 0),
        digitalTitles: inventoryRows.filter(r => r.status === "digital").length,
    };

    const STATUS_BADGE = {
        out: { label: "Out of stock", bg: "#f8d7da", color: "#721c24" },
        low: { label: "Low stock", bg: "#fff3cd", color: "#856404" },
        ok: { label: "In stock", bg: "#d4edda", color: "#155724" },
        digital: { label: "Digital", bg: "#cce5ff", color: "#004085" },
        unknown: { label: "No stock set", bg: "#f4f5f7", color: "#555" },
    };

    // ── Exports ──────────────────────────────────────────────
    function exportOrders() {
        const rows = filteredOrders.map(o => [
            o.id,
            dateOf(o) ? dateOf(o).toLocaleString() : "",
            STATUS_LABELS[o.status] || o.status || "",
            o.customerName || "",
            o.email || "",
            o.paymentMethod || "",
            money(o.subtotal).replace("$", ""),
            money(o.shippingCost).replace("$", ""),
            money(o.total).replace("$", ""),
            (o.items || []).map(i => `${i.quantity || 1}x ${i.title} (${i.format || "Paperback"})`).join("; "),
        ]);
        downloadCsv(
            `orders-${dateFilter}.csv`,
            ["Order", "Date", "Status", "Customer", "Email", "Payment", "Subtotal", "Shipping", "Total", "Items"],
            rows
        );
    }

    function exportInventory() {
        const rows = inventoryRows.map(r => [r.title, r.format, r.type, r.stock ?? "", r.sold, STATUS_BADGE[r.status].label]);
        downloadCsv("inventory.csv", ["Product", "Format", "Type", "In stock", "Sold (period)", "Status"], rows);
    }

    const ExportButton = ({ onClick }) => (
        <button onClick={onClick} className="admin-btn-secondary" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", fontSize: 13 }}>
            <FiDownload size={14} /> Export CSV
        </button>
    );

    return (
        <div className="admin-reports">
            <div className="admin-section-header" style={{ marginBottom: 30 }}>
                <div>
                    <h3>{view === "traffic" ? "Site Analytics" : view === "sales" ? "Sales Report" : "Inventory Report"}</h3>
                    <p style={{ margin: 0, fontSize: 13, color: "#888" }}>
                        Monitor your business performance and visitor behavior.
                    </p>
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    {/* View Switcher */}
                    <div style={{ background: "#f0f0f0", padding: 4, borderRadius: 10, display: "flex", gap: 4 }}>
                        <button
                            onClick={() => setView("traffic")}
                            className={`report-view-btn ${view === "traffic" ? "active" : ""}`}
                        >
                            <FiActivity size={14} style={{ marginRight: 6 }} /> Traffic
                        </button>
                        <button
                            onClick={() => setView("sales")}
                            className={`report-view-btn ${view === "sales" ? "active" : ""}`}
                        >
                            <FiDollarSign size={14} style={{ marginRight: 6 }} /> Sales
                        </button>
                        <button
                            onClick={() => setView("inventory")}
                            className={`report-view-btn ${view === "inventory" ? "active" : ""}`}
                        >
                            <FiPackage size={14} style={{ marginRight: 6 }} /> Inventory
                        </button>
                    </div>

                    {/* Global Date Filters */}
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <select
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            style={{ padding: "8px 12px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 14, background: "white" }}
                        >
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="month">This Month</option>
                            <option value="custom">Custom Range</option>
                            <option value="all">All Time</option>
                        </select>
                        {dateFilter === "custom" && (
                            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                                <input type="date" value={customRange.start} onChange={e => setCustomRange({...customRange, start: e.target.value})} style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 12 }} />
                                <input type="date" value={customRange.end} onChange={e => setCustomRange({...customRange, end: e.target.value})} style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #eee", fontSize: 12 }} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {view === "traffic" ? (
                /* ── Traffic View ───────────────────────────────── */
                <>
                    <div className="admin-stats">
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(26, 107, 74, 0.1)", color: "#1a6b4a" }}><FiActivity size={20} /></div>
                            <div className="stat-label">Visits ({dateFilter.replace('_', ' ')})</div>
                            <div className="stat-value">{trafficAgg.totalVisits}</div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(194, 163, 93, 0.1)", color: "#c2a35d" }}><FiGlobe size={20} /></div>
                            <div className="stat-label">Unique Countries</div>
                            <div className="stat-value">{sortedCountries.length}</div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(0, 123, 255, 0.1)", color: "#007bff" }}><FiBookOpen size={20} /></div>
                            <div className="stat-label">Product Views</div>
                            <div className="stat-value">{sortedProducts.reduce((a, b) => a + b[1], 0)}</div>
                        </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>
                        <div className="admin-card">
                            <div className="card-header-inner"><FiTrendingUp color="#1a6b4a" /> <h4>Top Pages ({dateFilter})</h4></div>
                            <div className="list-content">
                                {sortedPages.length === 0 ? <p className="admin-empty">No data yet.</p> : sortedPages.slice(0, 8).map(([path, count]) => (
                                    <div key={path} className="list-item">
                                        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{decodePath(path)}</span>
                                        <span className="badge-count">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="admin-card">
                            <div className="card-header-inner"><FiBookOpen color="#007bff" /> <h4>Product Interest</h4></div>
                            <div className="list-content">
                                {sortedProducts.length === 0 ? <p className="admin-empty">No products viewed.</p> : sortedProducts.slice(0, 8).map(([id, count]) => (
                                    <div key={id} className="list-item">
                                        <span>{allProducts[id]?.title || id}</span>
                                        <span className="badge-count" style={{ background: "#eef2ff", color: "#007bff" }}>{count} views</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="admin-card" style={{ marginTop: 24 }}>
                        <div className="card-header-inner"><FiMapPin color="#c2a35d" /> <h4>Visitors by Country ({dateFilter})</h4></div>
                        <div className="list-content" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 40px" }}>
                            {sortedCountries.length === 0 ? <p className="admin-empty">No data yet.</p> : sortedCountries.slice(0, 12).map(([country, count]) => (
                                <div key={country} className="list-item" style={{ border: "none", padding: "8px 0" }}>
                                    <span>{country}</span>
                                    <span className="badge-count" style={{ background: "#fdf8ef", color: "#c2a35d" }}>{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="admin-card" style={{ marginTop: 24 }}>
                        <div className="card-header-inner"><FiActivity color="#1a6b4a" /> <h4>Traffic Trend (Last 30 Days)</h4></div>
                        <div className="chart-bar-container">
                            {history.slice().reverse().map(day => (
                                <div key={day.id} title={`${day.id}: ${day.totalVisits} visits`} className="chart-bar" style={{ height: `${Math.min(100, (day.totalVisits / (Math.max(...history.map(h => h.totalVisits || 1)) || 1)) * 100)}%` }} />
                            ))}
                        </div>
                        <div className="chart-labels"><span>{history[history.length - 1]?.id}</span><span>Recent Trend</span><span>Today</span></div>
                    </div>
                </>
            ) : view === "sales" ? (
                /* ── Sales View ─────────────────────────────────── */
                <>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                        <ExportButton onClick={exportOrders} />
                    </div>

                    <div className="admin-stats">
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(26, 107, 74, 0.1)", color: "#1a6b4a" }}><FiDollarSign size={20} /></div>
                            <div className="stat-label">Revenue (Paid)</div>
                            <div className="stat-value">{money(sales.revenue)}</div>
                            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{sales.paidCount} paid order{sales.paidCount === 1 ? "" : "s"}</div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(217, 72, 15, 0.1)", color: "#d9480f" }}><FiActivity size={20} /></div>
                            <div className="stat-label">Awaiting Payment</div>
                            <div className="stat-value">{money(sales.awaiting)}</div>
                            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                                {(sales.byStatus.pending?.count || 0) + (sales.byStatus.pending_ath_verification?.count || 0)} pending order(s)
                            </div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(0, 123, 255, 0.1)", color: "#007bff" }}><FiShoppingBag size={20} /></div>
                            <div className="stat-label">Orders Placed</div>
                            <div className="stat-value">{filteredOrders.length}</div>
                            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{sales.cancelledCount} cancelled</div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(194, 163, 93, 0.1)", color: "#c2a35d" }}><FiPieChart size={20} /></div>
                            <div className="stat-label">Avg. Paid Order</div>
                            <div className="stat-value">{money(avgOrder)}</div>
                            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Top category: {topSalesCategories[0] ? topSalesCategories[0][0] : "—"}</div>
                        </div>
                    </div>

                    {/* Sales by date */}
                    <div className="admin-card" style={{ marginTop: 24 }}>
                        <div className="card-header-inner"><FiActivity color="#1a6b4a" /> <h4>Sales by Date</h4></div>
                        {dayRows.length === 0 ? <p className="admin-empty">No orders in this period.</p> : (
                            <div style={{ maxHeight: 380, overflowY: "auto" }}>
                                <table className="report-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th style={{ textAlign: "right" }}>Orders</th>
                                            <th style={{ textAlign: "right" }}>Paid</th>
                                            <th style={{ textAlign: "right" }}>Pending</th>
                                            <th style={{ textAlign: "right" }}>Cancelled</th>
                                            <th style={{ width: "25%" }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dayRows.map(r => (
                                            <tr key={r.date || "none"}>
                                                <td>{r.date || "No date"}</td>
                                                <td style={{ textAlign: "right" }}>{r.orders}</td>
                                                <td style={{ textAlign: "right", fontWeight: 700, color: "#1a6b4a" }}>{money(r.paid)}</td>
                                                <td style={{ textAlign: "right", color: "#d9480f" }}>{money(r.pending)}</td>
                                                <td style={{ textAlign: "right", color: "#888" }}>{r.cancelled}</td>
                                                <td>
                                                    <div className="report-bar-track">
                                                        <div className="report-bar-fill" style={{ width: `${maxDayPaid > 0 ? (r.paid / maxDayPaid) * 100 : 0}%` }} />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>
                        {/* Order status */}
                        <div className="admin-card">
                            <div className="card-header-inner"><FiPackage color="#007bff" /> <h4>By Order Status</h4></div>
                            {statusRows.length === 0 ? <p className="admin-empty">No orders.</p> : (
                                <table className="report-table">
                                    <thead>
                                        <tr><th>Status</th><th style={{ textAlign: "right" }}>Orders</th><th style={{ textAlign: "right" }}>Amount</th></tr>
                                    </thead>
                                    <tbody>
                                        {statusRows.map(r => (
                                            <tr key={r.status}>
                                                <td>{STATUS_LABELS[r.status] || r.status}</td>
                                                <td style={{ textAlign: "right" }}>{r.count}</td>
                                                <td style={{ textAlign: "right" }}>{money(r.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Payment methods */}
                        <div className="admin-card">
                            <div className="card-header-inner"><FiDollarSign color="#1a6b4a" /> <h4>By Payment Method</h4></div>
                            {paymentRows.length === 0 ? <p className="admin-empty">No orders.</p> : (
                                <table className="report-table">
                                    <thead>
                                        <tr><th>Method</th><th style={{ textAlign: "right" }}>Orders</th><th style={{ textAlign: "right" }}>Paid</th></tr>
                                    </thead>
                                    <tbody>
                                        {paymentRows.map(([method, r]) => (
                                            <tr key={method}>
                                                <td>{method}</td>
                                                <td style={{ textAlign: "right" }}>{r.count}</td>
                                                <td style={{ textAlign: "right" }}>{money(r.paid)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>
                        {/* Top Products Bought */}
                        <div className="admin-card">
                            <div className="card-header-inner"><FiPackage color="#1a6b4a" /> <h4>Products Bought</h4></div>
                            <div className="list-content">
                                {topSalesProducts.length === 0 ? <p className="admin-empty">No sales recorded for this period.</p> : topSalesProducts.slice(0, 8).map(([name, count]) => (
                                    <div key={name} className="list-item">
                                        <span>{name}</span>
                                        <span className="badge-count">{count} sold</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Top Categories */}
                        <div className="admin-card">
                            <div className="card-header-inner"><FiPieChart color="#007bff" /> <h4>Sales by Category</h4></div>
                            <div className="list-content">
                                {topSalesCategories.length === 0 ? <p className="admin-empty">No data.</p> : topSalesCategories.map(([cat, count]) => (
                                    <div key={cat} className="list-item">
                                        <span>{cat}</span>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <div style={{ width: 60, height: 6, background: "#eee", borderRadius: 3, overflow: "hidden" }}>
                                                <div style={{ height: "100%", background: "#007bff", width: `${(count / topSalesCategories[0][1]) * 100}%` }}></div>
                                            </div>
                                            <span className="badge-count" style={{ background: "#eef2ff", color: "#007bff" }}>{count}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sales Geography */}
                    <div className="admin-card" style={{ marginTop: 24 }}>
                        <div className="card-header-inner"><FiMapPin color="#c2a35d" /> <h4>Sales Geography (City/Country)</h4></div>
                        <div className="list-content" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 40px" }}>
                            {topSalesLocations.length === 0 ? <p className="admin-empty">No location data.</p> : topSalesLocations.slice(0, 12).map(([loc, count]) => (
                                <div key={loc} className="list-item" style={{ border: "none", padding: "8px 0" }}>
                                    <span>{loc}</span>
                                    <span className="badge-count" style={{ background: "#fdf8ef", color: "#c2a35d" }}>{count} order{count > 1 ? 's' : ''}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                /* ── Inventory View ─────────────────────────────── */
                <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
                        <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
                            Stock levels are current. "Sold" counts follow the date filter above.
                        </p>
                        <ExportButton onClick={exportInventory} />
                    </div>

                    <div className="admin-stats">
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(26, 107, 74, 0.1)", color: "#1a6b4a" }}><FiPackage size={20} /></div>
                            <div className="stat-label">Physical Units in Stock</div>
                            <div className="stat-value">{invSummary.physicalUnits}</div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(220, 53, 69, 0.1)", color: "#dc3545" }}><FiActivity size={20} /></div>
                            <div className="stat-label">Out of Stock</div>
                            <div className="stat-value">{invSummary.outOfStock}</div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(194, 163, 93, 0.1)", color: "#c2a35d" }}><FiActivity size={20} /></div>
                            <div className="stat-label">Low Stock (≤ {LOW_STOCK})</div>
                            <div className="stat-value">{invSummary.lowStock}</div>
                        </div>
                        <div className="admin-stat-card">
                            <div className="stat-icon" style={{ background: "rgba(0, 123, 255, 0.1)", color: "#007bff" }}><FiBookOpen size={20} /></div>
                            <div className="stat-label">Digital Titles</div>
                            <div className="stat-value">{invSummary.digitalTitles}</div>
                        </div>
                    </div>

                    <div className="admin-card" style={{ marginTop: 24 }}>
                        <div className="card-header-inner"><FiPackage color="#1a6b4a" /> <h4>Stock by Product & Format</h4></div>
                        {inventoryRows.length === 0 ? <p className="admin-empty">No products yet.</p> : (
                            <div style={{ overflowX: "auto" }}>
                                <table className="report-table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Format</th>
                                            <th>Type</th>
                                            <th style={{ textAlign: "right" }}>In stock</th>
                                            <th style={{ textAlign: "right" }}>Sold (period)</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inventoryRows.map(r => (
                                            <tr key={r.key}>
                                                <td>{r.title}</td>
                                                <td>{r.format}</td>
                                                <td>{r.type}</td>
                                                <td style={{ textAlign: "right" }}>{r.stock === null ? "—" : r.stock}</td>
                                                <td style={{ textAlign: "right" }}>{r.sold}</td>
                                                <td>
                                                    <span className="report-badge" style={{ background: STATUS_BADGE[r.status].bg, color: STATUS_BADGE[r.status].color }}>
                                                        {STATUS_BADGE[r.status].label}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}

            <style>{`
                .report-view-btn {
                    padding: 8px 16px;
                    border-radius: 8px;
                    border: none;
                    font-size: 13px;
                    font-weight: 700;
                    cursor: pointer;
                    background: transparent;
                    color: #666;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                }
                .report-view-btn.active {
                    background: white;
                    color: var(--green-primary);
                    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
                }
                .card-header-inner {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 20px;
                    border-bottom: 1.5px solid #f9f9f9;
                    padding-bottom: 15px;
                }
                .card-header-inner h4 { margin: 0; }
                .list-content { display: flex; flexDirection: column; gap: 12px; }
                .list-item { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f9f9f9; padding-bottom: 8px; }
                .list-item span { fontSize: 13px; color: #555; }
                .badge-count { fontSize: 12px; fontWeight: 700; background: #f0f7f4; color: #1a6b4a; padding: 2px 10px; borderRadius: 12px; }

                .report-table { width: 100%; border-collapse: collapse; font-size: 13px; }
                .report-table th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #888; padding: 8px 6px; border-bottom: 1.5px solid #f0f0f0; position: sticky; top: 0; background: white; }
                .report-table td { padding: 10px 6px; border-bottom: 1px solid #f9f9f9; color: #444; vertical-align: middle; }
                .report-bar-track { height: 6px; background: #eee; border-radius: 3px; overflow: hidden; }
                .report-bar-fill { height: 100%; background: #1a6b4a; border-radius: 3px; }
                .report-badge { font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 12px; white-space: nowrap; }

                .chart-bar-container { height: 100px; display: flex; alignItems: flex-end; gap: 8px; padding: 20px 0; }
                .chart-bar { flex: 1; background: #1a6b4a; borderRadius: 4px 4px 0 0; minHeight: 4px; opacity: 0.6; transition: all 0.2s; cursor: pointer; }
                .chart-bar:hover { opacity: 1; transform: scaleX(1.1); }
                .chart-labels { display: flex; justify-content: space-between; fontSize: 10px; color: #888; marginTop: 5px; textTransform: uppercase; letterSpacing: 1px; }

                .stat-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
            `}</style>
        </div>
    );
}
